import React, {useCallback, useMemo} from "react";
import {Button, Skeleton, Stack} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {SlowContent} from "../../components/loading-data/SlowContent";
import {useContractorVisitStore} from "../../api/ContractorVisitStore";
import {useFilter} from "../../util/FilterSerializer";
import {
    ContractorVisitReportFilter,
    contractorVisitReportFilterSerializer,
    filterContractorVisitReport,
} from "./ContractorVisitReportFilter";
import {VisitHeatMap} from "../employee-visit-report/VisitHeatMap";
import {useMessage} from "../../util/actions/Message";
import {defaultContractorVisitReportName, downloadContractorVisitCsv} from "./ExportContractorVisitCsv";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {ExportForDatesDialog, ExportForDatesForm} from "../employee-visit-report/ExportForDatesDialog";
import {filterOfficeVisitReport} from "../../util/visit/VisitReportSheetUtil";
import {VisitDataCoverageWarning} from "../employee-visit-report/VisitDataCoverageWarning";

export default function ContractorVisitReportPage() {
    const {remoteValue: unfiltered} = useContractorVisitStore();
    const [filter, setFilter] = useFilter(contractorVisitReportFilterSerializer);
    const filtered = useMemo(
        () => unfiltered.map(sheet => filterContractorVisitReport(sheet, filter)),
        [filter, unfiltered],
    );
    const message = useMessage();
    const handleExportCsv = useCallback(async () => {
        const sheet = await filtered.value;
        if (sheet.rows.length === 0) {
            message.error('No contractors found to export.');
        } else if (sheet.dates.length === 0) {
            message.error('No visits found for the selected dates.');
        } else {
            downloadContractorVisitCsv(sheet);
        }
    }, [filtered, message]);
    const handleExportForDatesCsv = useFormDialogAction<ExportForDatesForm, []>({
        title: 'Export for Dates',
        description: 'Select working days to include in the CSV report.',
        submitButtonText: 'Export',
        renderForm: ExportForDatesDialog,
        action: async openDialog => {
            const sheet = await filtered.value;
            if (sheet.rows.length === 0) {
                message.error('No contractors found to export.');
                return;
            }
            if (sheet.dates.length === 0) {
                message.error('Empty date range. Please select at least one working day to export.');
                return;
            }
            openDialog({
                initialValue: {
                    reportName: defaultContractorVisitReportName(),
                    selectedDates: new Set(),
                    sheet,
                },
                onSubmit: async form => downloadContractorVisitCsv(
                    filterOfficeVisitReport(sheet, date => form.selectedDates.has(date)),
                    form.reportName,
                ),
                successMessage: 'CSV report exported successfully.',
                errorMessage: 'Failed to export CSV report.',
            });
        },
    }, [filtered, message]);

    return (
        <Page
            header="Contractor Visits"
            counter={<StorePageCount totalValue={unfiltered} value={filtered}/>}
            actions={(
                <Stack direction="row" gap={2}>
                    <Button variant="outlined" color="primary" startIcon={<DownloadIcon/>} onClick={handleExportCsv}>
                        Export All
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon/>}
                        onClick={handleExportForDatesCsv}
                    >
                        Export for Dates
                    </Button>
                </Stack>
            )}
            filter={<ContractorVisitReportFilter filter={filter} setFilter={setFilter}/>}
        >
            <LoadingData
                totalValue={unfiltered}
                value={filtered}
                emptyMessage="No contractor visit data available."
                noMatchMessage="No contractor visits match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load contractor visits data. Please try again later."
            >{resolved => (
                <>
                    <VisitDataCoverageWarning dates={resolved.dates} dateRange={filter.dateRange}/>
                    {resolved.dates.length > 0 && (
                        <SlowContent loading={<Skeleton height="800px" variant="rectangular"/>}>
                            <VisitHeatMap data={resolved}/>
                        </SlowContent>
                    )}
                </>
            )}</LoadingData>
        </Page>
    );
}
